import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMailRoomsPage, fetchMailUnreadCount } from "../../apiCalls/messageApi";
import { setMailRoomsLoad } from "../../redux/mailSlice";
import {
  hasMoreMailRooms,
  mergeMailRoomPages,
  nextMailRoomPage,
} from "./mailRoomsPagination";

const MAIL_ROOMS_PAGE_SIZE = 16;

export function useInfiniteMailRooms({ dispatch, mailRooms, refreshToken, user }) {
  const userId = user?.id;
  const [searchQuery, setSearchQuery] = useState(
    () => localStorage.getItem("QUERYMAIL") || ""
  );
  const [page, setPage] = useState(0);
  const [hasMoreRooms, setHasMoreRooms] = useState(true);
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [roomsError, setRoomsError] = useState("");
  const scrollRootRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const roomsRef = useRef(mailRooms || []);

  useEffect(() => {
    roomsRef.current = mailRooms || [];
  }, [mailRooms]);

  const loadPage = useCallback(
    async (pageToLoad = 0, { reset = false } = {}) => {
      if (!userId || loadingRef.current) return [];

      loadingRef.current = true;
      setIsLoadingRooms(true);
      setRoomsError("");

      try {
        const nextRows = await fetchMailRoomsPage(
          user,
          null,
          MAIL_ROOMS_PAGE_SIZE,
          pageToLoad,
          searchQuery
        );
        const rows = Array.isArray(nextRows) ? nextRows : [];
        const mergedRows = reset
          ? rows
          : mergeMailRoomPages(roomsRef.current, rows);

        dispatch(setMailRoomsLoad(mergedRows));
        fetchMailUnreadCount(user, dispatch);
        setHasMoreRooms(hasMoreMailRooms(rows, MAIL_ROOMS_PAGE_SIZE));
        setPage(pageToLoad);
        return rows;
      } catch (error) {
        setRoomsError("Gesprekken konden niet geladen worden.");
        return [];
      } finally {
        loadingRef.current = false;
        setIsLoadingRooms(false);
      }
    },
    [dispatch, searchQuery, userId]
  );

  const resetRooms = useCallback(() => {
    setPage(0);
    setHasMoreRooms(true);
    scrollRootRef.current?.scrollTo({ top: 0 });
    return loadPage(0, { reset: true });
  }, [loadPage]);

  const loadMoreRooms = useCallback(() => {
    if (!hasMoreRooms || isLoadingRooms || loadingRef.current) return;
    loadPage(nextMailRoomPage(page), { reset: false });
  }, [hasMoreRooms, isLoadingRooms, loadPage, page]);

  useEffect(() => {
    resetRooms();
  }, [refreshToken, resetRooms]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRootRef.current;
    if (!sentinel || !root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreRooms();
        }
      },
      {
        root,
        rootMargin: "360px 0px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreRooms]);

  return {
    hasMoreRooms,
    isLoadingRooms,
    loadMoreRooms,
    resetRooms,
    roomsError,
    scrollRootRef,
    searchQuery,
    sentinelRef,
    setSearchQuery,
  };
}

export { MAIL_ROOMS_PAGE_SIZE };
